import { Link, Tooltip, Typography } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { manifestNumberLabel } from "encamp-shared/src/waste/shippingDocumentLabels";
import {
  WasteInvoiceLineKind,
  WasteInvoiceLinesTableQuery,
  WasteInvoiceLinesTableQueryVariables,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2, useLocalOmnisearch } from "hooks/omnisearch-v2";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";

import { scheduleOrderLink } from "../invoiceTrackingPeriod";
import { WASTE_INVOICE_LINES_QUERY } from "../queries";
import {
  formatInvoiceDate,
  formatInvoiceMoney,
  INVOICE_TRACKING_PAGE_SIZE,
  INVOICE_TRACKING_PAGE_SIZES,
  LINE_KIND_LABELS,
  NO_VALUE,
} from "../utils";

type InvoiceLine = WasteInvoiceLinesTableQuery["wasteInvoiceLines"]["items"][number];

const INITIAL_SORT: GridSortModel = [{ field: "serviceDate", sort: "asc" }];
const FILTER_KEYS: FilterKey[] = [
  { key: "facilityId", header: "Facility", filterKeyType: "wasteMultipleFacility" },
];
const FILTER_GROUPS: FilterGroup[] = [
  {
    name: "Invoice line",
    fields: [
      "kind",
      "description",
      "facilityId",
      "serviceDate",
      "serviceOrderNumber",
      "manifestTrackingNumber",
      "shippingDocumentNumber",
    ],
  },
];

function lineKindLabel(value: string): string {
  return Object.entries(LINE_KIND_LABELS).find(([kind]) => kind === value)?.[1] ?? value;
}

function InvoiceManifestLink({
  manifest,
  onClick,
}: {
  manifest: InvoiceLine["matchedProcessedManifest"];
  onClick: (manifestId: string) => void;
}) {
  return manifest ? (
    <Link component="button" type="button" onClick={() => onClick(manifest.id)}>
      {manifestNumberLabel(manifest)}
    </Link>
  ) : (
    NO_VALUE
  );
}

function InvoiceLineDescription({ description }: { description: InvoiceLine["description"] }) {
  return description ? (
    <Tooltip title={description}>
      <Typography
        variant="body2"
        noWrap
        sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
      >
        {description}
      </Typography>
    </Tooltip>
  ) : (
    NO_VALUE
  );
}

function ServiceOrderLink({ line, tenantId }: { line: InvoiceLine; tenantId: string }) {
  if (!line.serviceOrderNumber) return NO_VALUE;
  const to = scheduleOrderLink(tenantId, line.serviceDate, line.serviceOrderNumber);
  return to ? (
    <Link component={RouterLink} to={to}>
      {line.serviceOrderNumber}
    </Link>
  ) : (
    line.serviceOrderNumber
  );
}

function invoiceLineColumns(
  onManifestClick: (manifestId: string) => void,
  tenantId: string
): OmnisearchGridColDef<InvoiceLine>[] {
  return [
    {
      field: "manifestTrackingNumber",
      headerName: "Manifest",
      width: 165,
      valueGetter: (_, row) => row.matchedProcessedManifest?.manifestTrackingNumber ?? NO_VALUE,
      renderCell: ({ row }) => (
        <InvoiceManifestLink manifest={row.matchedProcessedManifest} onClick={onManifestClick} />
      ),
    },
    {
      field: "kind",
      headerName: "Charge type",
      width: 115,
      filterKeyType: "enum",
      enumValues: Object.values(WasteInvoiceLineKind),
      enumPresentationFunction: lineKindLabel,
      valueGetter: (_, row) => LINE_KIND_LABELS[row.kind],
    },
    {
      field: "description",
      headerName: "Description",
      minWidth: 210,
      flex: 1,
      valueGetter: (_, row) => row.description ?? NO_VALUE,
      renderCell: ({ row }) => <InvoiceLineDescription description={row.description} />,
    },
    {
      field: "facility",
      headerName: "Facility",
      minWidth: 170,
      flex: 0.8,
      filterable: false,
      valueGetter: (_, row) => row.facility?.name ?? NO_VALUE,
    },
    {
      field: "serviceDate",
      headerName: "Service date",
      width: 125,
      filterKeyType: "date",
      valueGetter: (_, row) => formatInvoiceDate(row.serviceDate),
    },
    {
      field: "billingQuantity",
      headerName: "Quantity",
      width: 120,
      type: "number",
      sortable: false,
      filterable: false,
      valueGetter: (_, row) =>
        row.billingQuantity == null
          ? NO_VALUE
          : `${row.billingQuantity.toLocaleString()} ${row.billingUnit ?? ""}`.trim(),
    },
    {
      field: "unitPrice",
      headerName: "Unit price",
      width: 115,
      type: "number",
      sortable: false,
      filterable: false,
      valueGetter: (_, row) => formatInvoiceMoney(row.unitPrice),
    },
    {
      field: "billedAmount",
      headerName: "Amount",
      width: 120,
      type: "number",
      filterable: false,
      valueGetter: (_, row) => formatInvoiceMoney(row.billedAmount),
    },
    {
      field: "serviceOrderNumber",
      headerName: "Service order",
      width: 145,
      valueGetter: (_, row) => row.serviceOrderNumber ?? NO_VALUE,
      renderCell: ({ row }) => <ServiceOrderLink line={row} tenantId={tenantId} />,
    },
    {
      field: "shippingDocumentNumber",
      headerName: "Shipping document",
      width: 165,
      sortable: false,
      valueGetter: (_, row) => row.shippingDocumentNumber ?? NO_VALUE,
    },
  ];
}

const getInvoiceLineItems = (data: WasteInvoiceLinesTableQuery) => data.wasteInvoiceLines.items;
const getInvoiceLineCount = (data: WasteInvoiceLinesTableQuery) => data.wasteInvoiceLines.count;

export function InvoiceLinesTable({
  invoiceId,
  tenantId,
  onManifestClick,
}: {
  invoiceId: string;
  tenantId: string;
  onManifestClick: (manifestId: string) => void;
}) {
  // The page's period selects invoices, not lines, so it stays out of this search.
  const omnisearchSource = useLocalOmnisearch();
  const onManifestClickRef = useRef(onManifestClick);
  onManifestClickRef.current = onManifestClick;
  const stableOnManifestClick = useCallback((manifestId: string) => {
    onManifestClickRef.current(manifestId);
  }, []);
  const columns = useMemo(
    () => invoiceLineColumns(stableOnManifestClick, tenantId),
    [stableOnManifestClick, tenantId]
  );
  const buildVariables = useCallback(
    (
      search: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): WasteInvoiceLinesTableQueryVariables => ({
      invoiceId,
      search,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((item) => ({ field: item.field, sort: item.sort })),
    }),
    [invoiceId]
  );

  return (
    <OmnisearchDataGridV2
      persistenceKey="waste-invoice-lines-table"
      tenantId={tenantId}
      query={WASTE_INVOICE_LINES_QUERY}
      getItems={getInvoiceLineItems}
      getCount={getInvoiceLineCount}
      buildVariables={buildVariables}
      columns={columns}
      pageSizeOptions={INVOICE_TRACKING_PAGE_SIZES}
      initialPageSize={INVOICE_TRACKING_PAGE_SIZE}
      initialSortModel={INITIAL_SORT}
      additionalFilterKeys={FILTER_KEYS}
      filterGroups={FILTER_GROUPS}
      omnisearchSource={omnisearchSource}
      disableRowSelectionOnClick
      noRowsMessage="No invoice lines match this search."
      sx={{ p: 0 }}
      gridSx={{ minHeight: 430 }}
    />
  );
}
